/* DRIVE IQ — ADVENTURE MODE V600.4 LOCAL FOOD AUTHORITY
   File: adventure-ranking.js

   Purpose:
   - Preserve the existing score(), rank(), top(), and registerScorer() API.
   - Reinforce category truth without replacing scan-engine hard filtering.
   - Favor trusted local places, strong district matches, and Worth the Drive.
   - Penalize obvious chain dominance without banning useful chain results.
   - Align ranking with scan confidence and existing __scanScore evidence.
   - Add batch-aware variety so repeated recommendation groups feel curated.
   - Make quick-stop categories strongly proximity-sensitive without hiding quality.
   - Keep coffee recommendations local and prevent distant metro results from outranking nearby shops.
   - Reward exact Geoapify nested category matches such as catering.cafe.coffee_shop.
   - Remove national chain restaurants from default Food results.
   - Preserve explicit brand searches so a user can still ask for a named chain.
   - Add stable tie-breaking using distance, category confidence, reviews, and original order.
   - Adds category personalities for Coffee, Food, Dessert, Water, Live Music, Nightlife, Outdoors, Family, Local Favorites, and Hidden Gems.
   - Adds scenic, waterfront, destination-landmark, and multi-experience bonuses.
   - Makes chain penalties contextual instead of blindly suppressing useful fallback options.
   - Expands category aliases so Water, Family, Dessert, Scenic, and Local Favorites use the intended ranking brain.
   - Adds market-aware distance behavior for destination categories.
   - Strengthens curated variety so Top Picks feel intentionally different.
*/

(function(window){
"use strict";

if(window.DriveIQAdventureRanking){return;}

var cfg=window.DriveIQAdventureConfig;
var utils=window.DriveIQAdventureUtils;

if(!cfg||!utils){
  throw new Error("Ranking requires config and utils.");
}

var scorers=[];

var CATEGORY_ALIASES={
  coffee_iq:"coffee",
  coffees:"coffee",
  cafe:"coffee",
  cafes:"coffee",

  food_iq:"food",
  restaurants:"food",
  restaurant:"food",
  dining:"food",

  dessert_iq:"dessert",
  desserts:"dessert",
  treats:"dessert",
  ice_cream:"dessert",

  local_favorites:"local_faves",
  local_favorite:"local_faves",
  local_hangout:"local_faves",
  local_faves:"local_faves",

  live_music_iq:"live_music",
  music:"live_music",

  water:"waterfront",
  water_iq:"waterfront",
  waterfront_iq:"waterfront",
  beach_access:"waterfront",
  beaches:"waterfront",

  family:"family_iq",
  family_fun:"family_iq",
  kids:"family_iq",

  outdoor:"outdoors",
  scenic:"outdoors",
  scenic_iq:"outdoors",

  hidden_gem:"hidden_gems",
  nightlife_iq:"nightlife"
};

var CATEGORY_RULES={
  coffee:{
    exact:[
      "coffee shop","coffeehouse","coffee house","espresso bar",
      "roaster","roastery","specialty coffee","single origin"
    ],
    related:[
      "coffee","espresso","cafe","café","tea house","bakery cafe",
      "pastry","breakfast"
    ],
    mismatch:[
      "steakhouse","seafood","sports bar","nightclub","gas station",
      "convenience store","grocery","supermarket"
    ]
  },
  food:{
    exact:[
      "restaurant","bistro","diner","eatery","kitchen","grill",
      "bbq","barbecue","burger","pizza","taco","seafood","steakhouse"
    ],
    related:[
      "food","deli","sandwich","bakery","cafe","café","brunch"
    ],
    mismatch:[
      "office","school","church","bank","hospital","storage",
      "parking lot"
    ]
  },
  dessert:{
    exact:[
      "ice cream","gelato","dessert","bakery","cupcake","donut",
      "doughnut","sweet shop","candy","chocolate"
    ],
    related:[
      "pastry","cafe","café","coffee","bakery cafe","milkshake"
    ],
    mismatch:[
      "steakhouse","sports bar","nightclub","office","bank","storage"
    ]
  },
  local_faves:{
    exact:[
      "local favorite","family owned","family-owned","independent",
      "neighborhood institution","historic institution","chef owned",
      "chef-owned","locally owned"
    ],
    related:[
      "local","historic","neighborhood","landmark","market",
      "brewery","museum","theater","theatre","district"
    ],
    mismatch:[
      "corporate office","warehouse","distribution center",
      "school district","storage"
    ]
  },
  live_music:{
    exact:[
      "live music","music venue","concert venue","honky tonk",
      "blues club","jazz club"
    ],
    related:[
      "concert","music hall","theater","theatre","bar","pub",
      "brewery","rooftop"
    ],
    mismatch:[
      "music store","record store","radio station","private studio",
      "school"
    ]
  },
  nightlife:{
    exact:[
      "nightclub","cocktail bar","lounge","dance club","late night",
      "rooftop bar"
    ],
    related:[
      "bar","pub","brewery","taproom","live music","rooftop"
    ],
    mismatch:[
      "coffee shop","breakfast","daycare","school","church",
      "hospital"
    ]
  },
  waterfront:{
    exact:[
      "waterfront","beachfront","oceanfront","riverfront","lakefront",
      "harbor","harbour","marina","boardwalk","pier","beach access",
      "boat ramp","fishing pier","water view"
    ],
    related:[
      "beach","water","river","lake","bay","gulf","dock","island",
      "sunset","scenic overlook","park"
    ],
    mismatch:[
      "retention pond","drainage pond","wastewater","water tower",
      "water treatment","utility","office","apartment"
    ]
  },
  outdoors:{
    exact:[
      "trail","nature preserve","scenic overlook","waterfall",
      "campground","beach","lake","park","boardwalk"
    ],
    related:[
      "nature","river","marina","garden","outdoors","sunset",
      "wildlife","forest"
    ],
    mismatch:[
      "indoor","office","shopping mall","apartment","storage"
    ]
  },
  family_iq:{
    exact:[
      "aquarium","zoo","mini golf","arcade","playground","children's museum",
      "kids museum","family attraction","water park","theme park","bowling"
    ],
    related:[
      "museum","park","attraction","ice cream","beach","boardwalk",
      "family friendly","kid friendly"
    ],
    mismatch:[
      "nightclub","adult entertainment","sports bar","casino",
      "office","warehouse"
    ]
  },
  hidden_gems:{
    exact:[
      "hidden gem","off the beaten path","independent","family owned",
      "family-owned","neighborhood favorite","secret spot"
    ],
    related:[
      "historic","local","garden","gallery","museum","market",
      "overlook","bakery","cafe","café"
    ],
    mismatch:[
      "walmart","target","mcdonald","starbucks","subway",
      "burger king","taco bell","chick-fil-a","chick fil a"
    ]
  }
};

var CHAIN_PENALTIES=[
  {terms:["mcdonald","burger king","taco bell"],penalty:14},
  {terms:["subway","wendy","applebee","chili's","olive garden"],penalty:12},
  {terms:["starbucks","dunkin","panera"],penalty:8},
  {terms:["walmart","target","best buy"],penalty:22}
];


/*
  V600.4 LOCAL FOOD AUTHORITY
  Food discovery should feel local, not like a generic interstate exit list.
  These brands are removed from default Food results, but remain available
  when the user explicitly searches for the brand by name.
*/
var NATIONAL_FOOD_CHAINS=[
  "mcdonald","burger king","wendy","taco bell","subway",
  "chick-fil-a","chick fil a","popeyes","kfc","kentucky fried chicken",
  "sonic","arbys","arby's","jack in the box","whataburger",
  "five guys","in-n-out","in n out","culver's","culvers",
  "dairy queen","dq grill","checkers","rally's","rallys",
  "white castle","krystal","bojangles","zaxby's","zaxbys",
  "raising cane's","raising canes","wingstop","buffalo wild wings",
  "little caesars","domino's","dominos","pizza hut","papa john",
  "marco's pizza","marcos pizza","hungry howie's","hungry howies",
  "applebee","chili's","chilis","olive garden","red lobster",
  "texas roadhouse","longhorn steakhouse","outback steakhouse",
  "cracker barrel","ihop","denny's","dennys","waffle house",
  "ruby tuesday","tgi friday","friday's","cheddar's","cheddars",
  "golden corral","bob evans","perkins","hooters",
  "panera","jimmy john","jersey mike","firehouse subs",
  "panda express","chipotle","qdoba","moe's southwest","moes southwest",
  "starbucks","dunkin","dunkin donuts","smoothie king",
  "tropical smoothie","jamba","auntie anne","cinnabon",
  "charleys cheesesteaks","sbarro","hardee's","hardees",
  "carl's jr","carls jr","church's chicken","churchs chicken",
  "captain d's","captain ds","long john silver","baskin-robbins",
  "cold stone","marble slab","insomnia cookies","great american cookies"
];

function explicitSearchText(context){
  return norm(
    context&&(
      context.query||
      context.searchQuery||
      context.searchTerm||
      context.userQuery||
      context.brandQuery||
      context.requestedBrand||
      context.explicitBrand||
      ""
    )
  );
}

function matchedNationalFoodChain(item){
  var textValue=itemText(item);
  var index;

  for(index=0;index<NATIONAL_FOOD_CHAINS.length;index+=1){
    if(textValue.indexOf(norm(NATIONAL_FOOD_CHAINS[index]))>-1){
      return NATIONAL_FOOD_CHAINS[index];
    }
  }

  return "";
}

function explicitBrandSearchAllows(item,context){
  var query=explicitSearchText(context);
  var chain=matchedNationalFoodChain(item);

  if(!query||!chain){
    return false;
  }

  return (
    query.indexOf(norm(chain))>-1||
    norm(chain).indexOf(query)>-1
  );
}

function shouldExcludeDefaultFoodChain(item,context){
  var requested=categoryKey(
    context&&(
      context.category||
      context.requestedCategory||
      context.scanCategory
    )
  );
  var matchedChain;

  if(requested!=="food"){
    return false;
  }

  if(explicitBrandSearchAllows(item,context)){
    return false;
  }

  matchedChain=matchedNationalFoodChain(item);

  if(matchedChain){
    item.__foodChainBlocked=true;
    item.__foodChainName=matchedChain;
    return true;
  }

  if(
    item&&
    item.isChain===true&&
    (
      item.isNationalChain===true||
      item.nationalChain===true||
      item.corporateChain===true||
      item.chainScope==="national"
    )
  ){
    item.__foodChainBlocked=true;
    item.__foodChainName=norm(
      item.brand||
      item.chain||
      item.parentBrand||
      item.name||
      "national chain"
    );
    return true;
  }

  return false;
}

var CATEGORY_DISTANCE_POLICIES={
  coffee:{ideal:2.5,acceptable:6,softLimit:9,hardLimit:12,perMileAfterIdeal:5.5},
  dessert:{ideal:3,acceptable:7,softLimit:10,hardLimit:14,perMileAfterIdeal:4.6},
  breakfast:{ideal:4,acceptable:8,softLimit:11,hardLimit:15,perMileAfterIdeal:4.2},
  pharmacy:{ideal:2.5,acceptable:6,softLimit:8,hardLimit:10,perMileAfterIdeal:6},
  food:{ideal:4,acceptable:10,softLimit:16,hardLimit:24,perMileAfterIdeal:2.4},
  live_music:{ideal:8,acceptable:20,softLimit:35,hardLimit:60,perMileAfterIdeal:.9},
  nightlife:{ideal:7,acceptable:18,softLimit:30,hardLimit:50,perMileAfterIdeal:1},
  waterfront:{ideal:10,acceptable:28,softLimit:50,hardLimit:80,perMileAfterIdeal:.45},
  outdoors:{ideal:12,acceptable:30,softLimit:55,hardLimit:90,perMileAfterIdeal:.55},
  family_iq:{ideal:7,acceptable:20,softLimit:32,hardLimit:50,perMileAfterIdeal:1},
  local_faves:{ideal:6,acceptable:18,softLimit:30,hardLimit:50,perMileAfterIdeal:1.1},
  hidden_gems:{ideal:8,acceptable:22,softLimit:40,hardLimit:70,perMileAfterIdeal:.75}
};

function clamp(value,min,max){
  return Math.max(min,Math.min(max,value));
}

function clean(value){
  return String(value==null?"":value)
    .replace(/\s+/g," ")
    .trim();
}

function norm(value){
  return utils.normText(value||"")
    .replace(/\s+/g," ")
    .trim();
}

function numberValue(value){
  var parsed=Number(value);
  return isFinite(parsed)?parsed:null;
}

function categoryKey(value){
  var key=norm(value)
    .replace(/[^a-z0-9]+/g,"_")
    .replace(/^_+|_+$/g,"");

  return CATEGORY_ALIASES[key]||key;
}

function itemText(item){
  return norm([
    item&&item.label,
    item&&item.name,
    item&&item.category,
    item&&item.primaryCategory,
    item&&item.userFacingCategory,
    item&&item.poiClass,
    item&&item.entityType,
    item&&item.brand,
    item&&item.chain,
    item&&item.parentBrand,
    item&&item.address,
    item&&item.full,
    item&&item.description,
    item&&item.summary,
    item&&item.tags&&item.tags.join
      ? item.tags.join(" ")
      : "",
    item&&item.types&&item.types.join
      ? item.types.join(" ")
      : ""
  ].join(" "));
}

function containsAny(textValue,terms){
  var index;

  for(index=0;index<(terms||[]).length;index+=1){
    if(textValue.indexOf(norm(terms[index]))>-1){
      return true;
    }
  }

  return false;
}

function ratingValue(item){
  return numberValue(
    item&&(
      item.rating||
      item.googleRating||
      item.averageRating
    )
  );
}

function reviewValue(item){
  var value=numberValue(
    item&&(
      item.reviewCount||
      item.userRatingsTotal||
      item.reviews||
      item.ratingCount
    )
  );

  return value===null?0:Math.max(0,value);
}

function distanceValue(item,context){
  var miles=numberValue(
    item&&(
      item.distanceMiles||
      item.distance_miles
    )
  );
  var meters;
  var center;
  var lat1;
  var lat2;
  var dLat;
  var dLng;
  var a;

  if(miles!==null){
    return Math.max(0,miles);
  }

  meters=numberValue(
    item&&(
      item.distanceMeters||
      item.distance_meters
    )
  );

  if(meters!==null){
    return Math.max(0,meters/1609.344);
  }

  center=context&&(
    context.center||
    context.location||
    context.scanCenter
  );

  if(
    !center||
    numberValue(item&&item.lat)===null||
    numberValue(item&&item.lng)===null||
    numberValue(center.lat)===null||
    numberValue(center.lng)===null
  ){
    return null;
  }

  lat1=Number(center.lat)*Math.PI/180;
  lat2=Number(item.lat)*Math.PI/180;
  dLat=(Number(item.lat)-Number(center.lat))*Math.PI/180;
  dLng=(Number(item.lng)-Number(center.lng))*Math.PI/180;

  a=
    Math.sin(dLat/2)*Math.sin(dLat/2)+
    Math.cos(lat1)*Math.cos(lat2)*
    Math.sin(dLng/2)*Math.sin(dLng/2);

  return 3958.7613*2*
    Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
}

function localSignalBonus(item){
  var textValue=itemText(item);
  var bonus=0;

  if(item&&item.localFavorite===true){bonus+=18;}
  if(item&&item.independent===true){bonus+=12;}
  if(item&&item.familyOwned===true){bonus+=12;}
  if(item&&item.historic===true){bonus+=8;}

  if(containsAny(textValue,[
    "family owned","family-owned","independent","chef owned",
    "chef-owned","neighborhood institution","historic institution"
  ])){
    bonus+=18;
  }else if(containsAny(textValue,[
    "local","neighborhood","historic","institution","landmark"
  ])){
    bonus+=10;
  }

  return Math.min(30,bonus);
}

function chainPenalty(item){
  var textValue=itemText(item);
  var penalty=0;

  CHAIN_PENALTIES.forEach(function(rule){
    if(containsAny(textValue,rule.terms)){
      penalty=Math.max(penalty,rule.penalty);
    }
  });

  if(item&&item.isChain===true){
    penalty=Math.max(penalty,14);
  }

  return penalty;
}

function categoryScore(item,context){
  var requested=categoryKey(
    context&&(
      context.category||
      context.requestedCategory||
      context.scanCategory
    )
  );
  var rules=CATEGORY_RULES[requested];
  var textValue=itemText(item);
  var truth=item&&item.__categoryTruth;

  if(truth&&truth.hardBlocked){
    return -100;
  }

  if(!rules){
    return truth&&truth.strongMatch
      ? 22
      : truth&&truth.allowMatch
        ? 12
        : 0;
  }

  if(containsAny(textValue,rules.mismatch)){
    return -40;
  }

  if(containsAny(textValue,rules.exact)){
    return 36;
  }

  if(containsAny(textValue,rules.related)){
    return 22;
  }

  if(truth&&truth.strongMatch){
    return 30;
  }

  if(truth&&truth.allowMatch){
    return 18;
  }

  if(truth&&truth.softMismatch){
    return -12;
  }

  return 0;
}



function scenicSignalScore(item){
  var textValue=itemText(item);
  var score=0;

  if(item&&item.waterfront===true){score+=22;}
  if(item&&item.scenic===true){score+=18;}
  if(item&&item.sunsetSpot===true){score+=14;}
  if(item&&item.hasView===true){score+=10;}

  if(containsAny(textValue,[
    "waterfront","beachfront","oceanfront","riverfront","lakefront",
    "harbor","harbour","marina","boardwalk","pier","water view"
  ])){
    score+=22;
  }

  if(containsAny(textValue,[
    "sunset","scenic overlook","panoramic","viewpoint","overlook"
  ])){
    score+=14;
  }

  if(containsAny(textValue,[
    "beach","bay","gulf","river","lake","island"
  ])){
    score+=8;
  }

  return Math.min(34,score);
}

function destinationLandmarkScore(item,context){
  var textValue=itemText(item);
  var score=0;

  if(item&&item.isLandmark===true){score+=18;}
  if(item&&item.isDistrictAnchor===true){score+=16;}
  if(item&&item.iconic===true){score+=16;}

  if(containsAny(textValue,[
    "harborwalk","destin harbor","broadway","beale street",
    "french quarter","disney springs","the wharf","silo square",
    "oxford square","downtown hernando","boardwalk"
  ])){
    score+=22;
  }else if(containsAny(textValue,[
    "landmark","historic district","downtown","town square",
    "village","harbor","harbour"
  ])){
    score+=10;
  }

  if(context&&context.scope==="near_me"&&item&&item.wrongDistrict===true){
    score-=18;
  }

  return score;
}

function experienceStackScore(item,context){
  var textValue=itemText(item);
  var requested=categoryKey(
    context&&(
      context.category||
      context.requestedCategory||
      context.scanCategory
    )
  );
  var signals=0;
  var score=0;

  [
    ["live music","concert","honky tonk","jazz","blues"],
    ["waterfront","beachfront","riverfront","lakefront","harbor","marina"],
    ["rooftop","patio","outdoor seating"],
    ["historic","landmark","local favorite","family owned","independent"],
    ["sunset","scenic","overlook","water view"],
    ["family friendly","kid friendly","playground","arcade","mini golf"]
  ].forEach(function(group){
    if(containsAny(textValue,group)){signals+=1;}
  });

  if(signals>=2){score+=8;}
  if(signals>=3){score+=8;}
  if(signals>=4){score+=6;}

  if(requested==="waterfront"){
    score+=Math.round(scenicSignalScore(item)*.7);
  }

  if(requested==="live_music"&&
     containsAny(textValue,["waterfront","rooftop","historic","district"])){
    score+=8;
  }

  if(requested==="food"&&
     containsAny(textValue,["waterfront","rooftop","historic","local favorite"])){
    score+=8;
  }

  return score;
}

function categoryPersonalityScore(item,context){
  var requested=categoryKey(
    context&&(
      context.category||
      context.requestedCategory||
      context.scanCategory
    )
  );
  var textValue=itemText(item);
  var score=0;

  if(requested==="coffee"){
    if(containsAny(textValue,["roaster","roastery","specialty coffee","single origin"])){score+=22;}
    if(containsAny(textValue,["independent","family owned","local favorite","neighborhood"])){score+=14;}
    if(containsAny(textValue,["pastry","bakery","breakfast","workspace","wifi","wi-fi"])){score+=8;}
    if(containsAny(textValue,["drive thru","drive-thru","mobile pickup","quick pickup"])){score+=5;}
  }

  if(requested==="food"){
    if(containsAny(textValue,["signature dish","chef owned","family owned","local favorite"])){score+=18;}
    if(containsAny(textValue,["waterfront","rooftop","historic","landmark"])){score+=10;}
  }

  if(requested==="dessert"){
    if(containsAny(textValue,["ice cream","gelato","bakery","donut","chocolate","dessert"])){score+=22;}
    if(containsAny(textValue,["local favorite","family owned","independent"])){score+=10;}
  }

  if(requested==="waterfront"){
    score+=scenicSignalScore(item);
    if(containsAny(textValue,["retention pond","drainage pond","water treatment","water tower"])){score-=70;}
  }

  if(requested==="live_music"){
    if(containsAny(textValue,["music venue","live music","concert venue","honky tonk","jazz club","blues club"])){score+=24;}
    if(containsAny(textValue,["tonight","calendar","stage"])){score+=8;}
  }

  if(requested==="nightlife"){
    if(containsAny(textValue,["cocktail bar","nightclub","lounge","rooftop bar","late night"])){score+=22;}
    if(containsAny(textValue,["live music","dance","dj","district"])){score+=8;}
  }

  if(requested==="outdoors"){
    if(containsAny(textValue,["trail","waterfall","nature preserve","scenic overlook","boardwalk","beach","lake"])){score+=20;}
    score+=Math.round(scenicSignalScore(item)*.5);
  }

  if(requested==="family_iq"){
    if(containsAny(textValue,["aquarium","zoo","mini golf","arcade","playground","children's museum","water park","theme park","bowling"])){score+=26;}
    if(containsAny(textValue,["family friendly","kid friendly","park","ice cream","beach"])){score+=12;}
  }

  if(requested==="hidden_gems"){
    if(containsAny(textValue,["hidden gem","off the beaten path","secret spot","independent","family owned"])){score+=24;}
    if(containsAny(textValue,["historic","local","neighborhood","gallery","garden","overlook"])){score+=10;}
  }

  if(requested==="local_faves"){
    if(containsAny(textValue,["local favorite","family owned","chef owned","independent","neighborhood institution","historic institution"])){score+=24;}
  }

  return score;
}

function contextualChainPenalty(item,context){
  var requested=categoryKey(
    context&&(
      context.category||
      context.requestedCategory||
      context.scanCategory
    )
  );
  var base=chainPenalty(item);
  var distance=distanceValue(item,context||{});
  var truth=item&&item.__categoryTruth;
  var localAlternatives=Number(context&&context.localAlternativeCount||0);

  if(base<=0){return 0;}

  if(
    requested==="coffee"&&
    distance!==null&&
    distance<=2.5&&
    localAlternatives<2
  ){
    return Math.round(base*.35);
  }

  if(
    truth&&truth.strongMatch&&
    localAlternatives<1
  ){
    return Math.round(base*.45);
  }

  return base;
}

function providerCategoryScore(item,context){
  var requested=categoryKey(
    context&&(
      context.category||
      context.requestedCategory||
      context.scanCategory
    )
  );
  var categories=[];
  var categoryText;

  if(item&&Array.isArray(item.categories)){
    categories=item.categories.map(norm);
  }

  categoryText=norm([
    item&&item.category,
    item&&item.primaryCategory,
    categories.join(" ")
  ].join(" "));

  if(requested==="coffee"){
    if(categoryText.indexOf("catering.cafe.coffee_shop")>-1){
      return 30;
    }

    if(categoryText.indexOf("catering.cafe.coffee")>-1){
      return 27;
    }

    if(categoryText.indexOf("catering.cafe")>-1){
      return 18;
    }
  }

  if(requested==="food"){
    if(categoryText.indexOf("catering.restaurant")>-1){
      return 24;
    }

    if(categoryText.indexOf("catering.")>-1){
      return 14;
    }
  }

  if(requested==="live_music"&&
     categoryText.indexOf("entertainment.music_venue")>-1){
    return 30;
  }

  if(requested==="waterfront"){
    if(categoryText.indexOf("beach")>-1||
       categoryText.indexOf("natural.water")>-1){
      return 28;
    }

    if(categoryText.indexOf("leisure.park")>-1||
       categoryText.indexOf("tourism.attraction")>-1){
      return 18;
    }

    if(categoryText.indexOf("catering.restaurant")>-1||
       categoryText.indexOf("catering.bar")>-1||
       categoryText.indexOf("catering.pub")>-1){
      return 8;
    }
  }

  if(requested==="dessert"){
    if(categoryText.indexOf("catering.cafe.ice_cream")>-1){
      return 28;
    }

    if(categoryText.indexOf("catering.cafe")>-1){
      return 14;
    }
  }

  if(requested==="family_iq"){
    if(categoryText.indexOf("entertainment")>-1||
       categoryText.indexOf("tourism.attraction")>-1||
       categoryText.indexOf("leisure.park")>-1){
      return 20;
    }
  }

  if(requested==="outdoors"&&
     (
       categoryText.indexOf("leisure.park")>-1||
       categoryText.indexOf("natural.")>-1||
       categoryText.indexOf("tourism.attraction")>-1
     )){
    return 22;
  }

  return 0;
}

function categoryDistanceScore(item,context){
  var requested=categoryKey(
    context&&(
      context.category||
      context.requestedCategory||
      context.scanCategory
    )
  );
  var policy=CATEGORY_DISTANCE_POLICIES[requested];
  var distance=distanceValue(item,context);

  if(distance===null||!policy){
    return 0;
  }

  if(distance<=policy.ideal){
    return 30-Math.min(8,distance*2.4);
  }

  if(distance<=policy.acceptable){
    return 18-
      ((distance-policy.ideal)*policy.perMileAfterIdeal);
  }

  if(distance<=policy.softLimit){
    return -8-
      ((distance-policy.acceptable)*
        policy.perMileAfterIdeal*1.4);
  }

  if(distance<=policy.hardLimit){
    return -34-
      ((distance-policy.softLimit)*
        policy.perMileAfterIdeal*2.0);
  }

  return -90-
    ((distance-policy.hardLimit)*
      policy.perMileAfterIdeal*2.5);
}

function districtScore(item,context){
  var requested=norm(
    context&&(
      context.district||
      context.districtName||
      context.centerLabel||
      context.destinationLabel
    )
  );
  var itemDistrict=norm(
    item&&(
      item.district||
      item.districtName||
      item.neighborhood||
      item.neighbourhood||
      item.areaName
    )
  );

  if(item&&item.insideDistrict===true){
    return 20;
  }

  if(item&&item.wrongDistrict===true){
    return -28;
  }

  if(!requested){
    return 0;
  }

  if(itemDistrict&&itemDistrict===requested){
    return 20;
  }

  if(
    itemDistrict&&
    (
      itemDistrict.indexOf(requested)>-1||
      requested.indexOf(itemDistrict)>-1
    )
  ){
    return 12;
  }

  if(item&&item.insideDistrict===false){
    return -14;
  }

  return 0;
}

function scanConfidenceScore(item){
  var scanScore=numberValue(item&&item.__scanScore);
  var truth=item&&item.__categoryTruth;
  var score=0;

  if(scanScore!==null){
    score+=clamp(scanScore,0,120)*0.38;
  }

  if(truth&&numberValue(truth.confidence)!==null){
    score+=clamp(Number(truth.confidence),0,1)*18;
  }

  return score;
}

function worthTheDriveScore(item){
  var value=numberValue(item&&item.worthTheDrive);

  if(value===null){
    value=numberValue(item&&item.worthTheDriveScore);
  }

  if(value===null){
    return 0;
  }

  return clamp(value,0,100)*0.52;
}

function baseScore(item,context){
  var score=0;
  var rating=ratingValue(item);
  var reviews=reviewValue(item);
  var distance=distanceValue(item,context||{});

  if(rating!==null){
    score+=clamp(rating,0,5)*18;
  }

  if(reviews>0){
    score+=Math.min(
      Math.log10(reviews+1)*11,
      28
    );
  }

  if(distance!==null){
    score+=Math.max(
      0,
      16-Math.min(distance*1.25,16)
    );
  }

  score+=categoryDistanceScore(
    item,
    context||{}
  );

  if(item&&item.openNow===true){score+=12;}
  if(item&&item.isOpen===true){score+=8;}

  score+=localSignalBonus(item);
  score+=worthTheDriveScore(item);
  score+=categoryScore(item,context||{});
  score+=providerCategoryScore(item,context||{});
  score+=categoryPersonalityScore(item,context||{});
  score+=destinationLandmarkScore(item,context||{});
  score+=experienceStackScore(item,context||{});
  score+=districtScore(item,context||{});
  score+=scanConfidenceScore(item);
  score-=contextualChainPenalty(item,context||{});

  return score;
}

function varietyKey(item,context){
  var requested=categoryKey(
    context&&(
      context.category||
      context.requestedCategory||
      context.scanCategory
    )
  );
  var textValue=itemText(item);

  if(requested==="coffee"){
    if(/roaster|roastery/.test(textValue)){return "roaster";}
    if(/bakery/.test(textValue)){return "bakery";}
    if(/tea/.test(textValue)){return "tea";}
    return "coffee";
  }

  if(requested==="food"){
    if(/bbq|barbecue/.test(textValue)){return "bbq";}
    if(/taco|mexican/.test(textValue)){return "mexican";}
    if(/pizza|italian/.test(textValue)){return "italian";}
    if(/seafood/.test(textValue)){return "seafood";}
    if(/burger/.test(textValue)){return "burger";}
    if(/breakfast|brunch/.test(textValue)){return "breakfast";}
  }

  if(requested==="live_music"){
    if(/honky tonk/.test(textValue)){return "honky_tonk";}
    if(/blues/.test(textValue)){return "blues";}
    if(/jazz/.test(textValue)){return "jazz";}
    if(/theater|theatre|concert/.test(textValue)){return "concert";}
  }

  if(requested==="waterfront"){
    if(/beach/.test(textValue)){return "beach";}
    if(/marina|harbor|harbour|dock/.test(textValue)){return "harbor";}
    if(/boardwalk|pier/.test(textValue)){return "boardwalk";}
    if(/restaurant|cafe|bar|pub/.test(textValue)){return "waterfront_dining";}
    if(/park|overlook/.test(textValue)){return "waterfront_park";}
  }

  if(requested==="family_iq"){
    if(/aquarium|zoo/.test(textValue)){return "animal_attraction";}
    if(/mini golf|arcade|bowling/.test(textValue)){return "games";}
    if(/museum/.test(textValue)){return "museum";}
    if(/park|playground|beach/.test(textValue)){return "outdoor_family";}
  }

  if(requested==="dessert"){
    if(/ice cream|gelato/.test(textValue)){return "ice_cream";}
    if(/bakery|donut|doughnut|pastry/.test(textValue)){return "bakery";}
    if(/chocolate|candy/.test(textValue)){return "sweets";}
  }

  return categoryKey(
    item&&(
      item.userFacingCategory||
      item.category||
      item.primaryCategory||
      item.poiClass||
      "other"
    )
  )||"other";
}

function diversityAdjustment(item,selected,context){
  var requested=categoryKey(
    context&&(
      context.category||
      context.requestedCategory||
      context.scanCategory
    )
  );
  var key=varietyKey(item,context||{});
  var duplicateCount=0;
  var name=norm(
    item&&(
      item.brand||
      item.chain||
      item.parentBrand||
      item.label||
      item.name
    )
  );

  (selected||[]).forEach(function(existing){
    if(varietyKey(existing,context||{})===key){
      duplicateCount+=1;
    }

    if(
      name&&
      name===norm(
        existing&&(
          existing.brand||
          existing.chain||
          existing.parentBrand||
          existing.label||
          existing.name
        )
      )
    ){
      duplicateCount+=2;
    }
  });

  if(duplicateCount===0){
    /*
      A useful chain may still appear once when it is genuinely close,
      but it receives no extra variety advantage over a local place.
    */
    return contextualChainPenalty(item,context||{})>0
      ? 1
      : 8;
  }

  if(contextualChainPenalty(item,context||{})>0){
    return -18*duplicateCount;
  }

  if(
    requested==="coffee"||
    requested==="live_music"
  ){
    return -8*duplicateCount;
  }

  return -11*duplicateCount;
}

function score(item,context){
  var total=baseScore(item,context||{});

  scorers.forEach(function(fn){
    try{
      total+=Number(fn(item,context||{})||0);
    }catch(error){}
  });

  item.__rankingScore=
    Math.round(total*100)/100;

  return item.__rankingScore;
}

function rank(results,context){
  var supplied=context||{};
  var list=(results||[]).filter(function(item){
    return !shouldExcludeDefaultFoodChain(item,supplied);
  });

  list.forEach(function(item,index){
    item.__originalIndex=index;
    score(item,supplied);
  });

  list.sort(function(a,b){
    var distanceA;
    var distanceB;
    var reviewsA;
    var reviewsB;

    if(b.__rankingScore!==a.__rankingScore){
      return b.__rankingScore-a.__rankingScore;
    }

    distanceA=distanceValue(a,supplied);
    distanceB=distanceValue(b,supplied);

    if(distanceA!==null&&distanceB!==null&&distanceA!==distanceB){
      return distanceA-distanceB;
    }

    reviewsA=reviewValue(a);
    reviewsB=reviewValue(b);

    if(reviewsA!==reviewsB){
      return reviewsB-reviewsA;
    }

    return a.__originalIndex-b.__originalIndex;
  });

  return list;
}

function curatedTop(results,count,context){
  var ranked=rank(results,context||{});
  var selected=[];
  var limit=Math.max(
    1,
    Number(count||cfg.scan.maximumResults||ranked.length)
  );

  while(
    ranked.length&&
    selected.length<limit
  ){
    var bestIndex=0;
    var bestScore=-Infinity;

    ranked.forEach(function(item,index){
      var adjusted=
        Number(item.__rankingScore||0)+
        diversityAdjustment(
          item,
          selected,
          context||{}
        );

      if(adjusted>bestScore){
        bestScore=adjusted;
        bestIndex=index;
      }
    });

    selected.push(
      ranked.splice(bestIndex,1)[0]
    );
  }

  selected.forEach(function(item,index){
    item.__curatedRank=index+1;
  });

  return selected;
}

function top(results,count,context){
  return curatedTop(
    results,
    count||cfg.scan.maximumResults,
    context||{}
  );
}

function registerScorer(fn){
  if(typeof fn!=="function"){
    throw new Error(
      "Ranking scorer must be a function."
    );
  }

  scorers.push(fn);

  return function(){
    var index=scorers.indexOf(fn);

    if(index>=0){
      scorers.splice(index,1);
    }
  };
}

window.DriveIQAdventureRanking={
  score:score,
  rank:rank,
  top:top,
  registerScorer:registerScorer,
  curatedTop:curatedTop,
  categoryPersonalityScore:categoryPersonalityScore,
  scenicSignalScore:scenicSignalScore,
  destinationLandmarkScore:destinationLandmarkScore,
  experienceStackScore:experienceStackScore,
  status:function(){
    return {
      build:"V600_4_LOCAL_FOOD_AUTHORITY",
      registeredScorerCount:scorers.length,
      categoryRuleCount:Object.keys(CATEGORY_RULES).length,
      nationalFoodChainCount:NATIONAL_FOOD_CHAINS.length,
      defaultFoodChainsExcluded:true
    };
  }
};

console.log(
  "[Drive IQ Adventure V600.4] Local Food Authority loaded"
);
})(window);
